import type Luminary from '@lumen5/luminary';
import type { MediaSourceData, Scene } from '@lumen5/luminary';

import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    setSceneWatermarkOverrideOperation,
    setSceneWatermarkPositionOperation,
} from 'js/src/luminary/luminary-operations';

export interface SceneWatermarkEdit {
    visibility?: boolean;
    watermark_source_index?: number;
    position?: string;
}

/** Why this edit can't apply to the scene, or null when it can. Changes nothing. */
export function validateSceneWatermarkEdit(
    luminary: Luminary,
    scene: Scene,
    sceneLabel: string,
    { visibility, watermark_source_index, position }: SceneWatermarkEdit,
): string | null {
    if (watermark_source_index === undefined && !position && visibility === undefined) {
        return 'Error: Please provide visibility (to show or hide the watermark), watermark_source_index (to change its source), position (to move it), or any combination of them';
    }

    // The watermark is part of the scene design; a design without one cannot be given one.
    if (!scene.watermark) {
        return `Error: No watermark available for editing on scene ${sceneLabel}.`;
    }

    const isShown = typeof scene.watermark.sourceData === 'object';
    if (
        visibility === true &&
        watermark_source_index === undefined &&
        !isShown &&
        defaultSourceIndex(luminary) === null
    ) {
        return `Error: Scene ${sceneLabel} has no watermark to bring back. Pass watermark_source_index to choose one: ${describeSources(luminary)}.`;
    }

    const sourceError = validateSourceIndex(luminary, watermark_source_index);
    if (sourceError) {
        return sourceError;
    }
    if (position && !scene.watermark.availablePositions.includes(position)) {
        return `Error: Invalid position "${position}". Available positions are: ${scene.watermark.availablePositions.join(', ')}`;
    }
    return null;
}

/**
 * Apply any combination of a scene's watermark source, position and visibility in one call.
 *
 * Every parameter is validated before anything changes, so an invalid one leaves the scene as it
 * was. Valid ones apply in a fixed order: source, then position, then visibility. Hiding wins over
 * a source given in the same call, and the result says so rather than silently dropping it.
 */
export function applySceneWatermarkEdit(
    luminary: Luminary,
    dispatch: AppDispatch,
    scene: Scene,
    sceneLabel: string,
    edit: SceneWatermarkEdit,
    parentActionGroup: string,
): string {
    const error = validateSceneWatermarkEdit(luminary, scene, sceneLabel, edit);
    if (error) {
        return error;
    }
    const { visibility, watermark_source_index, position } = edit;

    const showsWithoutSource = visibility === true && watermark_source_index === undefined;
    const shownSourceIndex = sourceIndexOf(luminary, scene.watermark.sourceData);
    const isShown = typeof scene.watermark.sourceData === 'object';
    const sourceToShow = showsWithoutSource && !isShown ? defaultSourceIndex(luminary) : null;

    const { setSceneWatermarkOverride, setSceneWatermarkPosition } = makeOperationsCallable(
        { setSceneWatermarkOverrideOperation, setSceneWatermarkPositionOperation },
        luminary,
        dispatch,
        () => null as unknown as RootState,
    );

    const hiding = visibility === false;
    const changes: string[] = [];
    const notes: string[] = [];

    if (watermark_source_index !== undefined) {
        if (hiding) {
            notes.push(
                `watermark source index ${watermark_source_index} was not applied because the watermark is hidden`,
            );
        } else {
            setSceneWatermarkOverride(scene.id, watermark_source_index, false, parentActionGroup);
            changes.push(`source index ${watermark_source_index}`);
        }
    }
    if (position) {
        setSceneWatermarkPosition(scene.id, position, false, parentActionGroup);
        changes.push(`position ${position}`);
    }
    if (hiding) {
        setSceneWatermarkOverride(scene.id, null, false, parentActionGroup);
        changes.push('hidden');
    } else if (sourceToShow !== null) {
        setSceneWatermarkOverride(scene.id, sourceToShow, false, parentActionGroup);
        changes.push(`shown with source index ${sourceToShow}`);
    } else if (showsWithoutSource) {
        changes.push(
            shownSourceIndex === null
                ? 'already shown'
                : `already shown with source index ${shownSourceIndex}`,
        );
    }

    const summary = `Success: Updated watermark in scene ${sceneLabel}: ${changes.join(', ')}.`;
    return notes.length > 0 ? `${summary} Note: ${notes.join('; ')}.` : summary;
}

/** The template source a resolved watermark shows, or null when it shows none of them. */
function sourceIndexOf(luminary: Luminary, sourceData: unknown): number | null {
    if (typeof sourceData !== 'object' || sourceData === null) {
        return null;
    }
    const { uuid } = sourceData as MediaSourceData;
    const index = luminary.template.watermarkSources.findIndex(
        (source) => source.sourceData === sourceData || source.sourceData.uuid === uuid,
    );
    return index === -1 ? null : index;
}

/** Why a watermark_source_index can't be used, or null when it can (or none was given). */
export function validateSourceIndex(
    luminary: Luminary,
    watermarkSourceIndex: number | undefined,
): string | null {
    const sourceCount = luminary.template.watermarkSources.length;
    if (
        watermarkSourceIndex === undefined ||
        (watermarkSourceIndex >= 0 && watermarkSourceIndex < sourceCount)
    ) {
        return null;
    }
    return sourceCount === 0
        ? `Error: watermark_source_index ${watermarkSourceIndex} is out of range: this template has no watermark sources.`
        : `Error: watermark_source_index ${watermarkSourceIndex} is out of range: use 0-${sourceCount - 1}.`;
}

/**
 * The source to bring back on a hidden scene when none is named: the video's own watermark, or
 * the template's only source. Null when that would be a guess.
 */
export function defaultSourceIndex(luminary: Luminary): number | null {
    const videoSourceIndex = sourceIndexOf(luminary, luminary.template.watermark.sourceData);
    if (videoSourceIndex !== null) {
        return videoSourceIndex;
    }
    return luminary.template.watermarkSources.length === 1 ? 0 : null;
}

export function describeSources(luminary: Luminary): string {
    const { watermarkSources } = luminary.template;
    if (watermarkSources.length === 0) {
        return 'this template has no watermark sources';
    }
    return watermarkSources
        .map((source, index) => `${index}: ${source.sourceData.title || 'untitled'}`)
        .join(', ');
}
